import { useId } from "react";
import { Field } from "@/types/Field";
import { AfImageValue } from "@/types/AfImage";
import { TableCellContent } from "../TableCell/TableCellContent";
import { useDelayedTooltip } from "@/hooks/useDelayedTooltip";
import { IconLock } from "@tabler/icons-react";
import { WorkflowFieldBadge } from "./WorkflowFieldBadge";

interface DataTypeInputProps {
  field: Field;
  onChangeValueAction: (value: string | string[]) => void;
  /**
   * `unknown`, not `string` — this component renders EVERY DataType, and two of
   * them are not scalars: MULTI_SELECT is an array and IMAGE is an object. The
   * declaration was `string` while the value forwarded here reached
   * `TableCellContent`, whose own prop is `unknown` and which coerces per
   * branch. So the narrow type described nothing real; what it did was invite
   * a caller to satisfy it with `String(...)`, which is exactly what happened
   * in `additionalFieldValue` and cost the drawer its image and multi-select
   * cells. Widening is permissive: every caller passing a string still fits.
   */
  value: unknown;
  label: string;
  disabled?: boolean;
  info?: string;
  copy?: boolean;
  viewOnly?: boolean;
  // Locked = a conditional ("filtered out") field. The input keeps its normal
  // box look but is non-interactive: a small lock sits by the label and a
  // full-cover button intercepts clicks so the parent can gate editing behind
  // a deliberate confirm step instead of letting the value change inline.
  locked?: boolean;
  lockedAriaLabel?: string;
  onLockedClick?: () => void;
  // Required field: renders a red asterisk after the label (marker only — the
  // admin popup doesn't hard-block; public form + backend enforce).
  required?: boolean;
  // Bound-aware validation message (e.g. "„Preis" darf höchstens 500 sein.")
  // routed from a failed save. Renders inline below the input, rings it red,
  // and marks the wrapper with data-af-error so the parent can scroll to it.
  error?: string;
  // Raw SELECT option values that are full (per-option capacity) — greyed out
  // in the picker unless already held by this guest.
  disabledOptions?: string[];
  // IMAGE-only: guest whose field this edits — enables the upload flow in
  // TCCImage (presign/confirm are guest-scoped). Absent on the create dialog.
  guestId?: string;
  // IMAGE-only: true when hosted by the guest CREATE dialog — the guest-less
  // hint then reads "upload after create" instead of "edit in guest details".
  isCreateContext?: boolean;
  // IMAGE-only: called INSTEAD of onChangeValueAction after a successful
  // upload confirm (the value is already persisted server-side) — the detail
  // sheet uses it to sync BOTH the form value and the pristine baseline so
  // Save doesn't light up dirty from the echo.
  onValuePersisted?: (value: AfImageValue) => void;
  // EMAIL-only: the box holds text that is not a storable address, so `value`
  // lags behind what the operator sees. A host's Save/Create button reads
  // this (via useInvalidFields) before it writes the lagging value.
  onInvalidChange?: (invalid: boolean) => void;
}

export const DataTypeInput: React.FC<DataTypeInputProps> = ({
  field,
  value,
  label,
  onChangeValueAction,
  disabled = false,
  info,
  copy = false,
  viewOnly,
  locked = false,
  lockedAriaLabel,
  onLockedClick,
  required = false,
  error,
  disabledOptions,
  guestId,
  isCreateContext,
  onValuePersisted,
  onInvalidChange,
}) => {
  // Hover-a-bit-longer tooltip: portalled bubble after a 500ms rest, anchored
  // below the label. No scroll/resize re-tracking, so it can't jitter or grow
  // while the pointer travels across a column of fields.
  const { ref, onMouseEnter, onMouseLeave, tooltip } =
    useDelayedTooltip<HTMLParagraphElement>(info);
  // The label is a plain <p>, not a <label htmlFor> — TableCellContent takes no
  // id and fans out to ~15 per-dataType controls (FormInput, OptionInput, date
  // pickers, image cell), so there is no single element to point at. Naming the
  // control REGION instead gives every custom field an accessible name in all
  // ~20 mount points without touching that tree. (Wrapping it in a <label>
  // would implicitly label the first labelable descendant — on the IMAGE cell
  // that is the file input, so a click on the label would open a file dialog.)
  const labelId = useId();

  return (
    <div
      className="flex flex-col space-y-1.5"
      data-af-error={error ? "true" : undefined}
    >
      <div className="flex flex-row items-center gap-1 max-w-full">
        <p
          id={labelId}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          ref={ref}
          className={`${
            info ? "cursor-help" : "cursor-default"
          } body-3-medium truncate min-w-0 max-w-full`}
        >
          {label}
        </p>
        {required && (
          <span className="font-error shrink-0 body-3-medium" aria-hidden>
            *
          </span>
        )}
        {locked && (
          <IconLock size={14} className="font-tertiary shrink-0" aria-hidden />
        )}
        {/* Phase B: marks fields an enabled automation will overwrite. Renders
            nothing outside the guest popup (no provider). */}
        <WorkflowFieldBadge fieldName={field.name} />
      </div>
      <div
        role="group"
        aria-labelledby={labelId}
        className={locked ? "relative" : undefined}
      >
        {/* Keep the real input so the locked box looks identical to an editable
            one, but make it genuinely inert: `inert` removes the inner control
            (text inputs AND select/date/boolean triggers) from the tab order
            and blocks focus, keydown, paste and activation — so the value can
            only change via the confirm flow, never by Tab+type or keyboard-
            opened dropdowns. `pointer-events-none` keeps the cursor/visuals;
            `aria-hidden` drops the inert box from the a11y tree so only the
            overlay button (which carries the locked label) is announced. */}
        <div
          inert={locked || undefined}
          aria-hidden={locked || undefined}
          className={
            locked ? "pointer-events-none select-none opacity-60" : undefined
          }
        >
          <TableCellContent
            field={field}
            padding="px-2"
            isSelected={!disabled}
            value={value}
            onChangeValueAction={onChangeValueAction}
            isEditMode={true}
            copy={copy}
            viewOnly={viewOnly}
            disabledOptions={disabledOptions}
            guestId={guestId}
            isCreateContext={isCreateContext}
            onValuePersisted={onValuePersisted}
            onInvalidChange={onInvalidChange}
            // Recolor the input's OWN border red on error rather than wrapping it
            // in a second box — a wrapper ring left the input's grey border
            // showing inside it (double border). FormInput hardcodes a trailing
            // `outline-quaternary`, so the color needs `!` (important) to win.
            // `error` is NOT passed down (the cell would render a duplicate
            // message); the message lives below.
            border={error ? "outline-error! border" : undefined}
          />
        </div>
        {locked && (
          <button
            type="button"
            aria-label={lockedAriaLabel}
            onClick={onLockedClick}
            className="absolute inset-0 h-full w-full cursor-pointer rounded-[10px]"
          />
        )}
      </div>
      {error && (
        <p role="alert" className="body-3-regular font-error">
          {error}
        </p>
      )}
      {tooltip}
    </div>
  );
};
